import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchControllerScopes,
  fetchCountryLanguages,
  fetchLanguages,
  setScope,
  setScopes,
} from "../../features/controllerCodeSlice";
import {
  setController,
  setControllers,
  fetchController,
} from "../../features/controllerSlice";
import { fetchConfig } from "../../features/configSlice";
import { getUrlParams, isRoleCOCO, isValidCocoSource } from "../../utils/utils";
import styles from "./styles.module.css";
import useTracker from "../../hooks/useTracker";
import EnvironmentStatusHeader from "../EnvironmentStatus/EnvironmentStatusHeader/EnvironmentStatusHeader";
import { HCP } from "../../utils/constants";
import HeaderDropDown from "./HeaderDropDown";
import useHideSidebar from "../../hooks/useHideSidebar";
import { PageCoco, PageIdentityDetails } from "../../utils/controls";
import useProfile from "../../hooks/useProfile";
import { storeTypes } from "../../features/typesSlice";
import { fetchControllerConfig } from "../../features/controllerConfigSlice";
import ThemeToggle from "../ThemeToggle";

const Header = () => {
  const isCocoPage = location.pathname.startsWith(`/ui${PageCoco}`);
  const isDetailsPage = location.pathname.startsWith(
    `/ui${PageIdentityDetails}`
  );
  const disableSelect = isCocoPage || isDetailsPage;
  const params = getUrlParams();
  const dispatch = useDispatch();
  const { tokenData } = useProfile();
  const roles = tokenData?.roles;
  const { scope, scopes, scopeStatus } = useSelector(
    (state) => state.controller
  );
  const {
    controllers,
    controller,
    loading: status,
  } = useSelector((state) => state.controllers);
  const { controllerCodeTracker, customerScopeTracker } = useTracker();
  const trackerDeatils = useSelector((state) => state?.TrackerReducer);
  const { hideSideBar } = useHideSidebar(location.pathname);
  const scopesLoaded = useMemo(
    () => ["succeeded", "failed"]?.includes(scopeStatus)|| ["failed"]?.includes(status),
    [scopeStatus,status]
  );
  const controllersLoaded = useMemo(
    () => ["succeeded", "failed"]?.includes(status),
    [status]
  );
  useEffect(() => {
    if (controller && scope) {
      dispatch(fetchLanguages({ controller, scope }));
      dispatch(fetchCountryLanguages({ controller, scope }));
      dispatch(fetchConfig({ controller, scope }));
    }
  }, [controller, scope]);

  const setControllerValue = (code, scope, track = false) => {
    dispatch(setController(code, scope));
    if (track) controllerCodeTracker(code,scope);
  };
  const setScopeValue = (value, track = false) => {
    dispatch(setScope(value));
    if (track) customerScopeTracker(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(storeTypes({ path: "", data: null }));
    if (name === "controller") {
      setControllerValue(value, "", 1);
      dispatch(setScope(""));
    } else if (name === "scope") {
      setScopeValue(value, 1);
    }
  };

  const isControlleCodeAvailable = (code) =>
    controllers?.includes(code);
  const isCustomerScopeAvailable = (scope) =>
    scopes?.some((item) => item === scope);
  const getCocoParamController = () =>
    params?.CountryCode && params?.Division
      ? `${params?.CountryCode}_${params?.Division}`?.toLowerCase()
      : "";
  const isDetailPageParams = (params) =>
    params?.controller &&
    params?.customer_scope &&
    params?.type &&
    params?.value;

  useEffect(() => {
    if (isRoleCOCO(roles)) {
      const controller_code = getCocoParamController();
      dispatch(setControllers(controller_code ? [{ controller_code }] : []));
      if (!controller_code) dispatch(setScopes([]));
    } else if (tokenData) dispatch(fetchController());
  }, [roles]);
  const isScopeAvailable = (scope) => scopes?.length && scopes?.includes(scope);

  useEffect(() => {
    if (!controllersLoaded || !controllers?.length) return;

    const urlParam = getUrlParams();

    const setController = (code, scope = "", value = 1) => {
      if (isControlleCodeAvailable(code)) {
        setControllerValue(code, scope, value);
      } else {
        setControllerValue(controllers[0], "", value);
      }
    };

    if (isValidCocoSource(urlParam?.SourceSystem)) {
      const paramControllerCode = getCocoParamController();
      setController(paramControllerCode);
    } else if (isDetailPageParams(urlParam)) {
      setController(urlParam?.controller);
    } else if (trackerDeatils?.controller_code) {
      setController(
        trackerDeatils.controller_code,
        trackerDeatils.customer_scope,
      );
    } else setControllerValue(controllers[0], "", 1);
  }, [controllers]);

  useEffect(() => {
    if (controllersLoaded && controllers?.length && controller) {
      if (isCocoPage && isRoleCOCO(roles)) dispatch(setScopes([HCP]));
      else dispatch(fetchControllerScopes(controller));
      dispatch(fetchControllerConfig({ controller }));
    }
  }, [controller]);

  useEffect(() => {
    if (
      controllersLoaded &&
      controllers?.length &&
      controller &&
      scopesLoaded &&
      scopes?.length
    ) {
      const urlParam = getUrlParams();
      if (isValidCocoSource(urlParam?.SourceSystem)) {
        setScopeValue(HCP, 1);
      } else if (isDetailPageParams(urlParam)) {
        if (isScopeAvailable(params?.customer_scope))
          setScopeValue(params?.customer_scope, 1);
        else setScopeValue(scopes[0], 1);
      } else {
        if (
          trackerDeatils?.customer_scope &&
          isCustomerScopeAvailable(trackerDeatils?.customer_scope)
        )
          setScopeValue(trackerDeatils?.customer_scope);
        else setScopeValue(scopes[0], 1);
      }
    }
  }, [scopes]);

  return (
    <section className="header">
      <div className={styles["header-container"]}>
        <a
          href={`${window.location.origin}/ui/`}
          className="d-flex gap-1 align-items-center text-decoration-none"
        >
          <img
            src="/ui/images/Truman-UI-Bayer-Logo.svg"
            alt="Bayer Logo"
            className={styles["navbar-logo"]}
          />
          <EnvironmentStatusHeader />
        </a>
        <div className={styles["controller-container"]}>
          <ThemeToggle />
          <HeaderDropDown
            name="controller"
            value={controller}
            disabled={disableSelect}
            loaded={controllersLoaded}
            onChange={handleChange}
            options={controllers}
            hideSideBar={hideSideBar}
          />
          <HeaderDropDown
            name="scope"
            value={scope}
            disabled={disableSelect}
            loaded={scopesLoaded}
            onChange={handleChange}
            options={scopes}
            hideSideBar={hideSideBar}
          />
        </div>
      </div>
    </section>
  );
};

export default Header;
